import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Panel, Loader } from "@/components/portal/Shared";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { UtensilsCrossed, Leaf, ChevronRight, X } from "lucide-react";

// Approximate homeland-region centroids for map markers.
const REGION_COORDS = {
  "Southeast": [33.5, -84.4], "Northeast & Great Lakes": [44.0, -77.5], "Great Plains": [41.0, -100.0],
  "Southwest": [35.2, -110.5], "Pacific Northwest": [47.6, -122.9], "California": [37.5, -120.2],
  "Arctic & Subarctic": [64.0, -108.0], "Caribbean": [18.2, -66.5], "Mesoamerica": [19.4, -99.1],
  "Pacific Islands": [20.8, -156.3], "South America": [11.5, -72.0],
  "Africa — North": [23.0, 5.0], "Africa — West": [12.0, -4.0], "Africa — East (Horn)": [9.1, 40.5],
  "Africa — East": [-1.3, 36.8], "Africa — East (Coast)": [-6.2, 39.2], "Africa — Central": [3.0, 18.0],
  "Africa — Great Lakes": [-2.0, 30.0], "Africa — Southern": [-29.5, 25.0],
};

export default function CuisineMap({ onNavigate }) {
  const [recipes, setRecipes] = useState(null);
  const [plants, setPlants] = useState([]);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/recipes"), api.get("/remedies")]).then(([r, p]) => {
      setRecipes(r.data.recipes); setPlants(p.data.plants);
    });
  }, []);

  const byRegion = useMemo(() => {
    const m = {};
    Object.keys(REGION_COORDS).forEach((r) => { m[r] = { recipes: [], plants: [] }; });
    (recipes || []).forEach((r) => { if (m[r.region]) m[r.region].recipes.push(r); });
    plants.forEach((p) => {
      if (m[p.region]) m[p.region].plants.push(p);
      else Object.keys(m).forEach((rg) => { if (p.region === "Multiple") m[rg]; }); // Multiple skipped from markers
    });
    return m;
  }, [recipes, plants]);

  if (!recipes) return <Loader />;
  const selData = sel ? byRegion[sel] : null;

  return (
    <div data-testid="module-cuisine-map">
      <PageHeader eyebrow="Explore" title="Cuisine & Plant Map"
        subtitle="Explore traditional recipes and plant knowledge by homeland region. Tap a region marker to see its foods and healing plants — then open any card to read more." />

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2 p-0 overflow-hidden">
          <div className="h-[62vh]" data-testid="cuisine-map-canvas">
            <MapContainer center={[20, -40]} zoom={2} scrollWheelZoom style={{ height: "100%", width: "100%" }} worldCopyJump>
              <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {Object.entries(REGION_COORDS).map(([region, coord]) => {
                const d = byRegion[region];
                const total = d.recipes.length + d.plants.length;
                if (!total) return null;
                const isSel = sel === region;
                return (
                  <CircleMarker key={region} center={coord} radius={Math.min(10 + total, 26)}
                    pathOptions={{ color: "#180D24", fillColor: isSel ? "#D4AF37" : "#59288E", fillOpacity: 0.85, weight: 2 }}
                    eventHandlers={{ click: () => setSel(region) }}>
                    <Tooltip>{region} · {d.recipes.length} recipes · {d.plants.length} plants</Tooltip>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          </div>
        </Panel>

        <Panel className="lg:col-span-1 p-5" data-testid="cuisine-region-panel">
          {!selData ? (
            <div className="text-center py-16 text-[#756D7E]">
              <UtensilsCrossed size={28} className="mx-auto text-[#D4AF37] mb-3" />
              Tap a marker on the map to explore a region's recipes and plants.
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-serif text-xl font-semibold text-[#180D24]">{sel}</h3>
                <button onClick={() => setSel(null)} className="text-[#9CA3AF] hover:text-[#180D24]"><X size={16} /></button>
              </div>

              <div className="flex items-center gap-2 text-[#59288E] font-semibold text-sm mb-2"><UtensilsCrossed size={15} /> Recipes ({selData.recipes.length})</div>
              <div className="space-y-1.5 mb-4 max-h-52 overflow-y-auto hp-scroll">
                {selData.recipes.map((r) => (
                  <button key={r.id} data-testid={`map-recipe-${r.id}`} onClick={() => onNavigate("tribal_recipes", { recipeId: r.id })}
                    className="w-full text-left flex items-center justify-between gap-2 rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 hover:border-[#D4AF37] transition-colors">
                    <span className="text-sm text-[#1A171D] truncate"><span className="font-semibold">{r.name}</span> · {r.tribe}</span>
                    <ChevronRight size={15} className="text-[#59288E] shrink-0" />
                  </button>
                ))}
                {selData.recipes.length === 0 ? <div className="text-xs text-[#756D7E]">No recipes indexed here yet.</div> : null}
              </div>

              <div className="flex items-center gap-2 text-[#166534] font-semibold text-sm mb-2"><Leaf size={15} /> Plants ({selData.plants.length})</div>
              <div className="space-y-1.5 max-h-52 overflow-y-auto hp-scroll">
                {selData.plants.map((p) => (
                  <button key={p.id} data-testid={`map-plant-${p.id}`} onClick={() => onNavigate("tribal_remedies", { plantId: p.id })}
                    className="w-full text-left flex items-center justify-between gap-2 rounded-lg border border-[#E4DCD3] bg-white px-3 py-2 hover:border-[#166534] transition-colors">
                    <span className="text-sm text-[#1A171D] truncate"><span className="font-semibold">{p.common_name}</span> · {p.plant_part}</span>
                    <ChevronRight size={15} className="text-[#166534] shrink-0" />
                  </button>
                ))}
                {selData.plants.length === 0 ? <div className="text-xs text-[#756D7E]">No plants indexed here yet.</div> : null}
              </div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
